/**
 * Confirmation before sending survey invitations.
 *
 * Sending email to members is irreversible, so this states the exact count and
 * shows who is about to be mailed rather than asking "are you sure?". It only ever
 * mails people with no `invite_sent_at`, so it cannot double-send — but the number
 * still has to be seen before it goes out.
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Skeleton,
  Typography,
} from '@mui/material';
import apiService from '../../services/api';

const TEAL = '#1A5F7A';

/** Mirrors the Lambda's own per-run ceiling. */
const MAX_PER_RUN = 100;

interface PendingRow {
  email: string;
  first_name?: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  /** Called after a send that actually mailed someone, so the page can reload. */
  onSent: (summary: { sent: number; remaining: number; failed: number }) => void;
  /** Which survey is being invited to. */
  campaign?: string | null;
  /** Its name, shown before sending — this dialog puts mail in front of people. */
  campaignTitle?: string | null;
}

const SendInvitesDialog: React.FC<Props> = ({ open, onClose, onSent, campaign = null, campaignTitle = null }) => {
  const [pending, setPending] = useState<PendingRow[] | null>(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiService.getPendingSurveyInvites(campaign || undefined);
      setPending(res?.would_send || []);
      setTotal(res?.pending ?? res?.would_send?.length ?? 0);
    } catch (e: any) {
      setError(e?.message || 'Could not work out who is waiting for an invite.');
      setPending(null);
    }
    setLoading(false);
  }, [campaign]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const send = async () => {
    setSending(true);
    setError(null);
    try {
      const res = await apiService.sendSurveyInvites(MAX_PER_RUN, campaign || undefined);
      const r = res?.result || res || {};
      const sent = Number(r.sent || 0);

      // "Reported success but sent 0" is treated as a failure, not a success.
      // MarketingCampaignsPage does the same, because a silent zero-send is
      // otherwise indistinguishable from a working send.
      if (sent === 0) {
        setError(
          r.failed?.length
            ? `Nothing was sent. ${r.failed.length} address${r.failed.length === 1 ? '' : 'es'} failed — the first was ${r.failed[0]?.email}.`
            : 'Nothing was sent. Nobody is currently waiting for an invite.'
        );
        setSending(false);
        await load();
        return;
      }

      onSent({
        sent,
        remaining: Number(r.remaining || 0),
        failed: r.failed?.length || 0,
      });
      onClose();
    } catch (e: any) {
      setError(
        e?.message?.includes('PERMISSION') || e?.message?.includes('403')
          ? 'You have view-only access to this area, so invitations cannot be sent.'
          : e?.message || 'The send failed.'
      );
    }
    setSending(false);
  };

  const shown = (pending || []).slice(0, 8);
  const overflow = Math.max((pending || []).length - shown.length, 0);
  const capped = total > MAX_PER_RUN;

  return (
    <Dialog
      open={open}
      // Locked during the send: closing mid-flight would leave the operator with no
      // idea how far it got, and the run continues server-side regardless.
      onClose={sending ? undefined : onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ fontFamily: "'Playfair Display', Georgia, serif", fontWeight: 700 }}>
        Send survey invitations
        {campaignTitle ? (
          // This dialog puts mail in front of real people, and the page can be showing
          // any survey. Naming the one that will actually be sent is the difference
          // between a considered send and an accident.
          <Typography component="span" sx={{ display: 'block', fontFamily: "'Montserrat', sans-serif", fontSize: 13, fontWeight: 600, color: '#6B7280', mt: 0.5 }}>
            {campaignTitle}
          </Typography>
        ) : null}
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2, fontSize: 13 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Skeleton variant="rectangular" height={140} sx={{ borderRadius: 2 }} />
        ) : total === 0 ? (
          <Typography sx={{ fontSize: 14, color: '#6B7280' }}>
            Nobody is waiting for an invite. Everyone on the list has already been
            sent one. Press <strong>Refresh list</strong> first if you are expecting
            new members.
          </Typography>
        ) : (
          <>
            <Typography sx={{ fontSize: 15, fontWeight: 600, color: '#111827', mb: 0.5 }}>
              {total} member{total === 1 ? '' : 's'} will be emailed.
            </Typography>
            <Typography sx={{ fontSize: 13, color: '#6B7280', mb: 2 }}>
              Only members who have never been sent an invitation. Anyone already
              invited, suppressed, bounced or finished is skipped automatically.
            </Typography>

            {capped && (
              <Alert severity="info" sx={{ mb: 2, fontSize: 13 }}>
                This run will send the first <strong>{MAX_PER_RUN}</strong>. Press Send
                again afterwards to work through the rest.
              </Alert>
            )}

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
              {shown.map((p) => (
                <Chip
                  key={p.email}
                  label={p.email}
                  size="small"
                  sx={{ fontSize: 11, bgcolor: '#F3F4F6' }}
                />
              ))}
              {overflow > 0 && (
                <Chip
                  label={`…and ${overflow} more`}
                  size="small"
                  sx={{ fontSize: 11, fontWeight: 600, bgcolor: '#E0F2FE', color: '#075985' }}
                />
              )}
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={sending} sx={{ textTransform: 'none', color: '#6B7280' }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={send}
          disabled={sending || loading || total === 0}
          sx={{ textTransform: 'none', bgcolor: TEAL, '&:hover': { bgcolor: '#14506B' } }}
        >
          {sending ? (
            <>
              <CircularProgress size={16} sx={{ color: '#fff', mr: 1 }} />
              Sending…
            </>
          ) : (
            `Send ${Math.min(total, MAX_PER_RUN) || ''} invitation${total === 1 ? '' : 's'}`.trim()
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SendInvitesDialog;
